import React, { useContext, useEffect, useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { InputText } from 'primereact/inputtext';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import 'primeflex/primeflex.css';
import { ToastContext } from 'app/context/ToastContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getDispatchNote, getNextDispatchNoteNr } from 'app/api/dispatchNoteApi';
import { Button } from 'primereact/button';
import * as Yup from 'yup';
import LazyTable from 'app/component/lazy-table';
import { formatDateFromInstant } from 'app/shared/util/date-utils'; // Make sure PrimeFlex is imported

const validationSchema = Yup.object({
  number: Yup.string().max(255, 'Max 255 znakov').required('Číslo je povinné'),
  documentName: Yup.string().max(255, 'Max 255 znakov').required('Meno je povinne'),
});

const CreateDispatchNote: React.FC = () => {
  const { id } = useParams<{ id?: string }>();

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [dispatchNote, setDispatchNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      getDispatchNote(id)
        .then(response => {
          if (response.data) {
            setDispatchNote(response.data);
          }
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      getNextDispatchNoteNr()
        .then(response => {
          if (response.data) {
            setDispatchNote({ number: response.data });
          }
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    }
  }, []);

  // Handle form submission
  const handleSubmit = (values: any) => {
    setIsSaving(true);

    let call;
    if (id) {
      call = axios.put(`/api/dispatch-notes/${id}`, values);
    } else {
      call = axios.post('/api/dispatch-notes', values);
    }

    call
      .then(response => {
        showSuccess('Úspech', 'Sprievodka úspešne uložená.');
        setDispatchNote(response.data);
        setIsSaving(false);
        navigate(`/dispatch-notes?p=${tempParameters}`);
      })
      .catch(error => {
        setIsSaving(false);
        showError('Chyba', error.response?.data?.detail);
        console.error('Error creating agent:', error);
      });
  };

  const onCancel = () => {
    navigate(`/dispatch-notes?p=${tempParameters}`);
  };

  return (
    <div className="card">
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}

      {!loading && (
        <>
          <h2>{id ? 'Upraviť dáta sprievodky' : 'Vytvoriť novú sprievodku'}</h2>
          <Formik
            initialValues={
              dispatchNote
                ? dispatchNote
                : {
                    number: '',
                    documentName: '',
                  }
            }
            validationSchema={validationSchema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ isSubmitting, setFieldValue, values, errors }) => {
              return (
                <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
                  <div className="grid">
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="number">Číslo sprievodky</label>
                        <Field disabled={true} name="number" as={InputText} />
                        <ErrorMessage name="number" component="div" className="p-error" />
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="documentName">Unikátne meno sprievodky</label>
                        <Field name="documentName" as={InputText} />
                        <ErrorMessage name="documentName" component="div" className="p-error" />
                      </div>
                    </div>
                  </div>

                  {id &&
                    LazyTable({
                      initialSortField: 'id',
                      createUrl: '',
                      blockInitialUrlParameters: true,
                      serverUrl: `/api/contracts/byDispatchNote/${id}`,
                      editActionAllowed: false,
                      infoActionAllowed: false,
                      searchAllowed: false,
                      globalFilterFields: [
                        // 'policyHolder.firstName',
                        // 'policyHolder.lastName',
                        // 'product.insuranceCompany.name',
                        // 'product.name',
                      ],
                      // externalLoading: isLoading,
                      columns: [
                        { field: 'id', header: 'ID zmluvy', sortable: true },
                        { field: 'createdDate', header: 'Vytvorené', sortable: true, formatter: formatDateFromInstant },
                        { field: 'policyHolder.firstName', header: 'Meno', sortable: true },
                        { field: 'policyHolder.lastName', header: 'Priezvisko ', sortable: true },
                        { field: 'policyHolder.businessName', header: 'Obchodné meno ', sortable: true },
                        { field: 'product.insuranceCompany.name', header: 'Spoločnosť', sortable: true },
                        { field: 'product.name', header: 'Produkt', sortable: true },
                      ],
                      // selectedItems: contracts,
                      // onSelectionChange: isAdminOrAssistant ? onSelectionChange : null,
                      // additionalHeaderButtons:
                      //   contracts?.length > 0 ? (
                      //     <Button
                      //       type="button"
                      //       icon="pi pi-download"
                      //       label="Generovať sprievodky"
                      //       className="p-button-info"
                      //       onClick={onGenerateDispatchNotes}
                      //     />
                      //   ) : null,
                    })}

                  {/* Submit Button */}
                  <Button
                    style={{ backgroundColor: '#e31818' }}
                    type="submit"
                    label="Uložiť"
                    icon="pi pi-check"
                    className="p-mt-3"
                    disabled={isSaving}
                  />
                  <Button
                    type="button"
                    style={{ backgroundColor: 'gray', marginTop: '10px' }}
                    label="Naspäť"
                    icon="pi pi-arrow-left"
                    className="p-button-secondary"
                    onClick={onCancel}
                  />
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </div>
  );
};

export default CreateDispatchNote;
